import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { SortOrder } from '@/types/common'
import { convertSortToApiFormat } from '@/utils'
import {
  DEFAULT_PAGE,
  MIN_SEARCH_LENGTH,
  TABLE_PAGE_SIZE,
} from '@/utils/constants'

import {
  PROJECT_DETAIL_QUERY_KEY,
  PROJECT_DETAIL_STALE_TIME,
  PROJECT_LIST_QUERY_KEY,
  QUERY_KEY_PROJECT_LIST,
} from './constants'
import { projectApi } from './services'
import {
  CreateProjectRequest,
  ProjectFilters,
  UpdateProjectRequest,
} from './types'

export const useProjectList = (params: {
  search?: string
  page?: number
  rowsPerPage?: number
  sort?: {
    column: string
    direction: SortOrder
  }
  filters?: ProjectFilters
  enabled?: boolean
}) => {
  const {
    search = '',
    page = DEFAULT_PAGE,
    rowsPerPage = TABLE_PAGE_SIZE,
    sort,
    filters,
    enabled = true,
  } = params
  const query = search.trim().length >= MIN_SEARCH_LENGTH ? search.trim() : ''
  const sortBy = convertSortToApiFormat(
    sort || { column: null, direction: null },
    '-created_at'
  )
  const filterBy: Record<string, string[]> = {}
  if (filters?.companyId) {
    filterBy.company_id = [filters.companyId]
  }

  return useQuery({
    queryKey: PROJECT_LIST_QUERY_KEY([
      query,
      page,
      rowsPerPage,
      sortBy,
      JSON.stringify(filters || {}),
    ]),
    queryFn: async () => {
      const response = await projectApi.getProjectList({
        q: query,
        offset: page * rowsPerPage,
        limit: rowsPerPage,
        sort_by: sortBy,
        filter_by: Object.keys(filterBy).length > 0 ? filterBy : undefined,
      })
      return response
    },
    staleTime: Infinity,
    enabled,
  })
}

export const useProjectById = (id: string | undefined) => {
  return useQuery({
    queryKey: PROJECT_DETAIL_QUERY_KEY(id || ''),
    queryFn: async () => {
      if (!id) {
        throw new Error('Project ID is required')
      }
      const response = await projectApi.getProjectById(id)
      return response.data[0]
    },
    enabled: !!id,
    staleTime: PROJECT_DETAIL_STALE_TIME,
  })
}

export const useCreateProject = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateProjectRequest) => projectApi.createProject(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_PROJECT_LIST],
        refetchType: 'active',
      })
    },
  })
}

export const useUpdateProject = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string | number
      data: UpdateProjectRequest
    }) => projectApi.updateProject(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_PROJECT_LIST],
        refetchType: 'active',
      })
      queryClient.invalidateQueries({
        queryKey: PROJECT_DETAIL_QUERY_KEY(variables.id),
        refetchType: 'active',
      })
    },
  })
}

export const useDeleteProject = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string | number) => projectApi.deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_PROJECT_LIST],
        refetchType: 'active',
      })
    },
  })
}

export const useProjectInfinite = (params: {
  search?: string
  sort?: {
    column: string
    direction: SortOrder
  }
}) => {
  const { search = '', sort } = params
  const query = search.trim().length >= MIN_SEARCH_LENGTH ? search.trim() : ''
  const sortBy = convertSortToApiFormat(
    sort || { column: null, direction: null },
    '+company_name, +project_name'
  )
  return useInfiniteQuery({
    queryKey: PROJECT_LIST_QUERY_KEY([query, sortBy]),
    queryFn: ({ pageParam = DEFAULT_PAGE }) =>
      projectApi.getProjectList({
        q: query,
        sort_by: sortBy,
        limit: TABLE_PAGE_SIZE,
        offset: pageParam * TABLE_PAGE_SIZE,
        search_type: 'cross_hierarchy',
        variant: 'dropdown',
      }),
    getNextPageParam: (lastPage, allPages) => {
      const hasMore = lastPage?.pagination?.has_next ?? false
      return hasMore ? allPages.length : undefined
    },
    initialPageParam: DEFAULT_PAGE,
  })
}
